import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type ScopeType = 'PersonalTenant' | 'CommonTenant';
type PlanStatus = 'Enabled' | 'Disabled';
type QuotaMode = 'PreCheckLimit' | 'PostUsageBilling' | 'RetentionPolicy';
type ChargePolicyType = 'FixedDeny' | 'PointDeduct' | 'UpgradeHint' | 'PurchaseAddon' | 'PaidExtension';

interface ScopeTab {
  label: string;
  value: ScopeType;
}

interface QuotaPlanChargeRule {
  ruleType?: ChargePolicyType;
  name?: string;
  description?: string;
  billingUnit?: string;
  unitPrice?: string;
  currency?: string;
  effectValue?: string;
  effectUnit?: string;
  recommend?: boolean;
  effectiveDate?: number;
  sort?: number;
  status?: PlanStatus;
  lastUpdateTime?: number;
}

interface QuotaPlanWarningRule {
  thresholdPercent?: number;
  titleTemplate?: string;
  contentTemplate?: string;
  status?: PlanStatus;
  lastUpdateTime?: number;
}

interface QuotaPlanItem {
  itemId?: string;
  planId?: string;
  categoryCode?: string;
  categoryName?: string;
  quotaCode?: string;
  quotaName?: string;
  quotaDescription?: string;
  unit?: string;
  freeLimitValue?: string;
  defaultValue?: string;
  sort?: number;
  quotaMode?: QuotaMode;
  chargePolicyType?: ChargePolicyType;
  status?: PlanStatus;
  version?: number;
  lastUpdateTime?: number;
  chargeRules?: QuotaPlanChargeRule[];
}

interface QuotaPlan {
  planId?: string;
  scopeType?: ScopeType;
  planName?: string;
  planDescription?: string;
  status?: PlanStatus;
  sort?: number;
  lastUpdateTime?: number;
  warningRules?: QuotaPlanWarningRule[];
  warningRule?: QuotaPlanWarningRule[] | QuotaPlanWarningRule | null;
  items?: QuotaPlanItem[];
  overviewStats?: PlanOverviewStat[];
  collapsedSummaryStats?: CollapsedPlanStat[];
  warningSummary?: string;
}

interface PlanOverviewStat {
  key: string;
  title: string;
  value: string;
  hint: string;
  tone: 'blue' | 'green' | 'orange' | 'violet';
}

interface CollapsedPlanStat {
  key: string;
  title: string;
  value: string;
}

interface PlanInfoDraft {
  planName: string;
  planDescription: string;
  status: PlanStatus;
}

interface QuotaPlanAuditLog {
  createTime?: number;
  quotaCode?: string;
  quotaName?: string;
  fieldCode?: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  impactTenantCount?: number;
  operatorId?: string;
  operatorName?: string;
  version?: number;
}

interface QuotaAuditDiffRow {
  key: string;
  label: string;
  oldValue: string;
  newValue: string;
  changed: boolean;
}

interface AuditDrawerContext {
  mode: 'plan' | 'item';
  title: string;
  subtitle: string;
  payload: {
    planId?: string;
    itemId?: string;
  };
}

type QuotaIconType = 'deployment-unit' | 'user' | 'tags' | 'robot' | 'hdd' | 'branches' | 'team' | 'message' | 'inbox';

@Component({
  selector: 'quota-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzDrawerModule,
    NzEmptyModule,
    NzIconModule,
    NzModalModule,
    NzPaginationModule,
    NzSpinModule,
    NzTagModule,
    TestIdDirective
  ],
  templateUrl: './quota-plan.component.html',
  styleUrls: ['./quota-plan.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotaPlanComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly router = inject(Router);

  readonly scopeTabs: ScopeTab[] = [
    { label: this.t('quota.scope.personal'), value: 'PersonalTenant' },
    { label: this.t('quota.scope.team'), value: 'CommonTenant' }
  ];
  readonly loadingPlanPlaceholders = [0, 1];
  readonly loadingQuotaPlaceholders = [0, 1, 2, 3];

  loading = false;
  activeScope: ScopeType = 'PersonalTenant';
  plans: QuotaPlan[] = [];
  normalizedPlans: QuotaPlan[] = [];
  collapsedPlanIds = new Set<string>();
  expandedWarningPlanIds = new Set<string>();
  editingPlanId: string | null = null;
  editingPlanInfoId: string | null = null;
  savingPlanId: string | null = null;
  savingPlanInfoId: string | null = null;
  planInfoModalVisible = false;
  activePlanInfo: QuotaPlan | null = null;
  warningRuleDraftMap: Record<string, QuotaPlanWarningRule[]> = {};
  planInfoDraftMap: Record<string, PlanInfoDraft> = {};
  auditDrawerVisible = false;
  auditDrawerLoading = false;
  auditLogs: QuotaPlanAuditLog[] = [];
  auditDrawerContext: AuditDrawerContext | null = null;
  auditPageIndex = 0;
  auditPageSize = 20;
  auditTotalCount = 0;
  auditHasNextPage = false;
  auditRefreshTime = 0;
  private readonly auditFieldOrder = ['freeLimitValue', 'defaultValue', 'chargePolicyType', 'quotaMode', 'status', 'chargeRules'];

  t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  ngOnInit(): void {
    this.activeScope = this.normalizeScopeType(this.route.snapshot.queryParamMap.get('scopeType'));
    this.loadPlans();
  }

  switchScope(scope: ScopeType): void {
    if (this.activeScope === scope || this.loading) {
      return;
    }

    this.activeScope = scope;
    this.loadPlans();
  }

  reload(): void {
    this.loadPlans();
  }

  openPlanAuditDrawer(plan: QuotaPlan, event?: Event): void {
    event?.stopPropagation();
    if (!plan.planId) {
      return;
    }

    this.auditDrawerContext = {
      mode: 'plan',
      title: plan.planName || this.t('quota.plan.audit.plan-title'),
      subtitle: this.t('quota.plan.audit.plan-subtitle'),
      payload: {
        planId: plan.planId
      }
    };
    this.resetAuditPagination();
    this.auditDrawerVisible = true;
    this.loadAuditLogs();
  }

  openItemAuditDrawer(plan: QuotaPlan, item: QuotaPlanItem, event?: Event): void {
    event?.stopPropagation();
    if (!plan.planId || !item.itemId) {
      return;
    }

    this.auditDrawerContext = {
      mode: 'item',
      title: item.quotaName || this.t('quota.plan.audit.item-title'),
      subtitle: this.t('quota.plan.audit.item-subtitle', { planName: plan.planName || this.t('quota.plan.current-plan') }),
      payload: {
        planId: plan.planId,
        itemId: item.itemId
      }
    };
    this.resetAuditPagination();
    this.auditDrawerVisible = true;
    this.loadAuditLogs();
  }

  closeAuditDrawer(): void {
    this.auditDrawerVisible = false;
  }

  onAuditPageChange(page: number): void {
    const nextPageIndex = Math.max(page - 1, 0);
    if (this.auditDrawerLoading || nextPageIndex === this.auditPageIndex) {
      return;
    }

    this.auditPageIndex = nextPageIndex;
    this.loadAuditLogs();
  }

  onAuditPageSizeChange(size: number): void {
    if (this.auditDrawerLoading || size === this.auditPageSize) {
      return;
    }

    this.auditPageSize = size;
    this.auditPageIndex = 0;
    this.loadAuditLogs();
  }

  get pageTitle(): string {
    return this.i18n.fanyi('quota.center.title');
  }

  get pageSubtitle(): string {
    return this.i18n.fanyi('quota.center.subtitle');
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByScope(_: number, tab: ScopeTab): ScopeType {
    return tab.value;
  }

  trackByPlanGroup(_: number, plan: QuotaPlan): string {
    return plan.planId ?? '';
  }

  trackByPlan(_: number, item: QuotaPlanItem): string {
    return item.itemId ?? `${item.planId ?? ''}-${item.quotaCode ?? ''}`;
  }

  trackByAuditLog(index: number, log: QuotaPlanAuditLog): string {
    return `${log.createTime ?? 0}-${log.fieldCode ?? 'field'}-${log.version ?? 'v'}-${index}`;
  }

  trackByAuditDiff(_: number, row: QuotaAuditDiffRow): string {
    return row.key;
  }

  isPlanCollapsed(plan: QuotaPlan): boolean {
    return plan.planId ? this.collapsedPlanIds.has(plan.planId) : false;
  }

  togglePlanCollapse(plan: QuotaPlan): void {
    if (!plan.planId) {
      return;
    }

    if (this.collapsedPlanIds.has(plan.planId)) {
      this.collapsedPlanIds.delete(plan.planId);
      return;
    }

    this.collapsedPlanIds.add(plan.planId);
  }

  getStatusLabel(status?: PlanStatus): string {
    if (!status) {
      return '--';
    }

    return status === 'Enabled' ? this.t('quota.status.enabled') : this.t('quota.status.disabled');
  }

  getStatusColor(status?: PlanStatus): string {
    return status === 'Enabled' ? 'green' : 'default';
  }

  isEditingPlanInfo(plan: QuotaPlan): boolean {
    return !!plan.planId && this.editingPlanInfoId === plan.planId;
  }

  isSavingPlanInfo(plan: QuotaPlan): boolean {
    return !!plan.planId && this.savingPlanInfoId === plan.planId;
  }

  startEditPlanInfo(plan: QuotaPlan): void {
    if (!plan.planId) {
      return;
    }

    this.editingPlanInfoId = plan.planId;
    this.activePlanInfo = plan;
    this.planInfoModalVisible = true;
    this.planInfoDraftMap[plan.planId] = this.createPlanInfoDraft(plan);
    this.cdr.markForCheck();
  }

  cancelEditPlanInfo(plan?: QuotaPlan): void {
    if (plan?.planId) {
      delete this.planInfoDraftMap[plan.planId];
    }
    this.editingPlanInfoId = null;
    this.activePlanInfo = null;
    this.planInfoModalVisible = false;
    this.cdr.markForCheck();
  }

  getPlanInfoDraft(plan: QuotaPlan): PlanInfoDraft {
    if (!plan.planId) {
      return this.createPlanInfoDraft(plan);
    }

    if (!this.planInfoDraftMap[plan.planId]) {
      this.planInfoDraftMap[plan.planId] = this.createPlanInfoDraft(plan);
    }

    return this.planInfoDraftMap[plan.planId];
  }

  savePlanInfo(plan: QuotaPlan): void {
    if (!plan.planId || this.isSavingPlanInfo(plan)) {
      return;
    }

    const draft = this.getPlanInfoDraft(plan);
    const planName = draft.planName.trim();
    const planDescription = draft.planDescription.trim();
    if (!planName) {
      this.message.error(this.t('quota.plan.message.plan-name-required'));
      return;
    }

    this.savingPlanInfoId = plan.planId;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_V1_UPDATE, {
        planId: plan.planId,
        scopeType: this.activeScope,
        planName,
        planDescription
      })
      .pipe(
        finalize(() => {
          this.savingPlanInfoId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          this.applyPlanInfo(plan.planId!, { planName, planDescription });
          delete this.planInfoDraftMap[plan.planId!];
          this.editingPlanInfoId = null;
          this.activePlanInfo = null;
          this.planInfoModalVisible = false;
          this.message.success(this.t('quota.plan.message.plan-info-saved'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  getChargePolicyLabel(type?: ChargePolicyType): string {
    const policyMap: Record<ChargePolicyType, string> = {
      FixedDeny: this.t('quota.policy.fixed'),
      PointDeduct: this.t('quota.policy.deduct-points'),
      UpgradeHint: this.t('quota.policy.upgrade-prompt'),
      PurchaseAddon: this.t('quota.policy.purchase-extension'),
      PaidExtension: this.t('quota.policy.paid-extension')
    };

    return type ? (policyMap[type] ?? type) : '--';
  }

  getChargePolicyColor(type?: ChargePolicyType): string {
    const colorMap: Record<ChargePolicyType, string> = {
      FixedDeny: 'red',
      PointDeduct: 'green',
      UpgradeHint: 'blue',
      PurchaseAddon: 'orange',
      PaidExtension: 'gold'
    };

    return type ? (colorMap[type] ?? 'default') : 'default';
  }

  formatQuotaValue(value?: string, unit?: string): string {
    if (!value && value !== '0') {
      return '--';
    }

    const unitLabel = this.getUnitLabel(unit);
    const formattedValue = this.formatDisplayNumber(value);
    return unitLabel ? `${formattedValue} ${unitLabel}` : formattedValue;
  }

  formatAuditValue(value?: string): string {
    const text = String(value ?? '').trim();
    return text || '--';
  }

  getAuditDiffRows(log: QuotaPlanAuditLog): QuotaAuditDiffRow[] {
    const oldData = this.parseAuditData(log.oldValue);
    const newData = this.parseAuditData(log.newValue);

    if (this.isAuditWarningRuleList(oldData) || this.isAuditWarningRuleList(newData)) {
      return this.getWarningRuleAuditDiffRows(oldData, newData, log.fieldName);
    }

    if (this.isAuditRuleList(oldData) || this.isAuditRuleList(newData)) {
      return this.getChargeRuleAuditDiffRows(oldData, newData, log.fieldName);
    }

    if (!this.isAuditRecord(oldData) || !this.isAuditRecord(newData)) {
      return [
        {
          key: 'value',
          label: log.fieldName || this.t('quota.detail.audit.field-change'),
          oldValue: this.formatAuditValue(log.oldValue),
          newValue: this.formatAuditValue(log.newValue),
          changed: this.formatAuditValue(log.oldValue) !== this.formatAuditValue(log.newValue)
        }
      ];
    }

    const keys = this.getAuditDiffKeys(oldData, newData);

    return keys
      .map(key => {
        const oldValue = this.formatAuditFieldValue(key, oldData[key]);
        const newValue = this.formatAuditFieldValue(key, newData[key]);
        return {
          key,
          label: this.getAuditFieldLabel(key),
          oldValue,
          newValue,
          changed: oldValue !== newValue
        };
      })
      .filter(row => row.changed);
  }

  isEditingWarningRules(plan: QuotaPlan): boolean {
    return !!plan.planId && this.editingPlanId === plan.planId;
  }

  isSavingWarningRules(plan: QuotaPlan): boolean {
    return !!plan.planId && this.savingPlanId === plan.planId;
  }

  isWarningPanelExpanded(plan: QuotaPlan): boolean {
    return !!plan.planId && this.expandedWarningPlanIds.has(plan.planId);
  }

  toggleWarningPanel(plan: QuotaPlan): void {
    if (!plan.planId || this.isEditingWarningRules(plan)) {
      return;
    }

    if (this.expandedWarningPlanIds.has(plan.planId)) {
      this.expandedWarningPlanIds.delete(plan.planId);
      return;
    }

    this.expandedWarningPlanIds.add(plan.planId);
  }

  startEditPlanWarningRules(plan: QuotaPlan): void {
    if (!plan.planId) {
      return;
    }

    this.expandedWarningPlanIds.add(plan.planId);
    this.editingPlanId = plan.planId;
    this.warningRuleDraftMap[plan.planId] = this.cloneWarningRules(plan.warningRules);
  }

  cancelEditPlanWarningRules(plan?: QuotaPlan): void {
    if (plan?.planId) {
      delete this.warningRuleDraftMap[plan.planId];
    }
    this.editingPlanId = null;
    this.cdr.markForCheck();
  }

  getPlanWarningRuleDraft(plan: QuotaPlan): QuotaPlanWarningRule[] {
    if (!plan.planId) {
      return [];
    }

    if (!this.warningRuleDraftMap[plan.planId]) {
      this.warningRuleDraftMap[plan.planId] = this.cloneWarningRules(plan.warningRules);
    }

    return this.warningRuleDraftMap[plan.planId];
  }

  savePlanWarningRules(plan: QuotaPlan): void {
    if (!plan.planId || this.isSavingWarningRules(plan)) {
      return;
    }

    const normalizedRules = this.preserveWarningRuleStatuses(plan.warningRules, this.getPlanWarningRuleDraft(plan));
    const validationMessage = this.validateWarningRules(normalizedRules);
    if (validationMessage) {
      this.message.error(validationMessage);
      return;
    }

    this.savingPlanId = plan.planId;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_V1_UPDATE, {
        planId: plan.planId,
        scopeType: this.activeScope,
        warningRule: normalizedRules,
        warningRules: normalizedRules
      })
      .pipe(
        finalize(() => {
          this.savingPlanId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          this.applyPlanWarningRules(plan.planId!, normalizedRules);
          delete this.warningRuleDraftMap[plan.planId!];
          this.editingPlanId = null;
          this.message.success(this.t('quota.plan.message.warning-rules-saved'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  private buildPlanOverviewStats(plan: QuotaPlan): PlanOverviewStat[] {
    const items = plan.items ?? [];
    const preferredCodes = [
      'SERVICE_ACCOUNT_LIMIT',
      'EMPLOYEE_LIMIT',
      'TAG_LIMIT',
      'ASSISTANT_LIMIT',
      'STORAGE_SPACE',
      'PRO_SERVICE_ACCOUNT_LIMIT',
      'CHANNEL_BINDING_LIMIT',
      'CUSTOMER_LIMIT',
      'MESSAGE_RETENTION',
      'ATTACHMENT_RETENTION'
    ];

    const toneMap: Record<string, PlanOverviewStat['tone']> = {
      EMPLOYEE_LIMIT: 'blue',
      CUSTOMER_LIMIT: 'blue',
      SERVICE_ACCOUNT_LIMIT: 'green',
      PRO_SERVICE_ACCOUNT_LIMIT: 'green',
      ASSISTANT_LIMIT: 'green',
      CHANNEL_BINDING_LIMIT: 'orange',
      TAG_LIMIT: 'orange',
      STORAGE_SPACE: 'violet',
      MESSAGE_RETENTION: 'violet',
      ATTACHMENT_RETENTION: 'violet'
    };

    const sortedItems = [...items].sort((a, b) => {
      const indexA = preferredCodes.indexOf(a.quotaCode ?? '');
      const indexB = preferredCodes.indexOf(b.quotaCode ?? '');
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return sortedItems.slice(0, 5).map((item, index) => ({
      key: item.quotaCode ?? `overview-${index}`,
      title: item.quotaName || this.t('quota.plan.default-item-title'),
      value: this.formatQuotaValue(item.freeLimitValue, item.unit),
      hint: item.quotaDescription || item.quotaCode || '--',
      tone: toneMap[item.quotaCode ?? ''] ?? (['blue', 'green', 'orange', 'violet'][index % 4] as PlanOverviewStat['tone'])
    }));
  }

  private buildCollapsedSummaryStats(plan: QuotaPlan): CollapsedPlanStat[] {
    const items = plan.items ?? [];
    const summaryConfig = [
      { code: 'SERVICE_ACCOUNT_LIMIT', title: this.t('quota.plan.summary.service-account') },
      { code: 'EMPLOYEE_LIMIT', title: this.t('quota.plan.summary.employee') },
      { code: 'TAG_LIMIT', title: this.t('quota.plan.summary.tag') },
      { code: 'STORAGE_SPACE', title: this.t('quota.plan.summary.storage') }
    ];

    return summaryConfig
      .map(config => {
        const item = items.find(current => current.quotaCode === config.code);
        if (!item) {
          return null;
        }

        return {
          key: config.code,
          title: item.quotaName || config.title,
          value: this.formatQuotaValue(item.freeLimitValue, item.unit)
        };
      })
      .filter((stat): stat is CollapsedPlanStat => !!stat);
  }

  getQuotaIconType(quotaCode?: string): QuotaIconType {
    const iconMap: Record<string, QuotaIconType> = {
      SERVICE_ACCOUNT_LIMIT: 'deployment-unit',
      PRO_SERVICE_ACCOUNT_LIMIT: 'deployment-unit',
      EMPLOYEE_LIMIT: 'user',
      CUSTOMER_LIMIT: 'team',
      TAG_LIMIT: 'tags',
      ASSISTANT_LIMIT: 'robot',
      STORAGE_SPACE: 'hdd',
      CHANNEL_BINDING_LIMIT: 'branches',
      MESSAGE_RETENTION: 'message',
      ATTACHMENT_RETENTION: 'inbox'
    };

    return iconMap[quotaCode ?? ''] ?? 'deployment-unit';
  }

  getQuotaTone(quotaCode?: string): PlanOverviewStat['tone'] {
    const toneMap: Record<string, PlanOverviewStat['tone']> = {
      EMPLOYEE_LIMIT: 'blue',
      CUSTOMER_LIMIT: 'blue',
      SERVICE_ACCOUNT_LIMIT: 'green',
      PRO_SERVICE_ACCOUNT_LIMIT: 'green',
      ASSISTANT_LIMIT: 'green',
      CHANNEL_BINDING_LIMIT: 'orange',
      TAG_LIMIT: 'orange',
      STORAGE_SPACE: 'violet',
      MESSAGE_RETENTION: 'violet',
      ATTACHMENT_RETENTION: 'violet'
    };

    return toneMap[quotaCode ?? ''] ?? 'blue';
  }

  openDetail(plan: QuotaPlan, item: QuotaPlanItem): void {
    if (!plan.planId || !item.itemId) {
      return;
    }

    this.router.navigate(['/quota-templates', this.activeScope, plan.planId, item.itemId]);
  }

  private loadAuditLogs(): void {
    const payload = this.auditDrawerContext?.payload ?? {};
    this.auditDrawerLoading = true;
    this.auditLogs = [];
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_V1_AUDIT_LIST, {
        ...payload,
        pageIndex: this.auditPageIndex,
        pageSize: this.auditPageSize,
        orderBy: 'createTime',
        direction: 'desc',
        refreshTime: this.auditRefreshTime
      })
      .pipe(
        finalize(() => {
          this.auditDrawerLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.auditLogs = [];
            this.auditTotalCount = 0;
            this.auditHasNextPage = false;
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          const result = res.data;
          if (Array.isArray(result)) {
            this.auditLogs = [...result].sort((a, b) => (b?.createTime ?? 0) - (a?.createTime ?? 0));
            this.auditTotalCount = result.length;
            this.auditHasNextPage = false;
            return;
          }

          const logs = Array.isArray(result?.items) ? result.items : [];
          this.auditLogs = logs;
          this.auditTotalCount = Number(result?.totalCount ?? logs.length ?? 0);
          this.auditHasNextPage = !!result?.hasNextPage;
          this.auditPageIndex = Number(result?.pageIndex ?? this.auditPageIndex ?? 0);
          this.auditRefreshTime = Number(result?.refreshTime ?? this.auditRefreshTime ?? 0);
        },
        error: () => {
          this.auditLogs = [];
          this.auditTotalCount = 0;
          this.auditHasNextPage = false;
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  private resetAuditPagination(): void {
    this.auditPageIndex = 0;
    this.auditPageSize = 20;
    this.auditTotalCount = 0;
    this.auditHasNextPage = false;
    this.auditRefreshTime = 0;
    this.auditLogs = [];
  }

  private loadPlans(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.http
      .post(ConstantUtil.ADMIN_QUOTA_PLAN_V1_LIST, { scopeType: this.activeScope })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.plans = [];
            this.normalizedPlans = [];
            this.editingPlanId = null;
            this.editingPlanInfoId = null;
            this.activePlanInfo = null;
            this.planInfoModalVisible = false;
            this.warningRuleDraftMap = {};
            this.planInfoDraftMap = {};
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          this.plans = (res.data ?? []).map((plan: QuotaPlan) => this.normalizePlan(plan));
          this.normalizedPlans = this.buildNormalizedPlans(this.plans);
          this.editingPlanId = null;
          this.editingPlanInfoId = null;
          this.activePlanInfo = null;
          this.planInfoModalVisible = false;
          this.warningRuleDraftMap = {};
          this.planInfoDraftMap = {};
        },
        error: () => {
          this.plans = [];
          this.normalizedPlans = [];
          this.editingPlanId = null;
          this.editingPlanInfoId = null;
          this.activePlanInfo = null;
          this.planInfoModalVisible = false;
          this.warningRuleDraftMap = {};
          this.planInfoDraftMap = {};
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  private normalizeScopeType(scopeType?: string | null): ScopeType {
    return scopeType === 'CommonTenant' || scopeType === 'COMMON_TENANT' ? 'CommonTenant' : 'PersonalTenant';
  }

  private normalizeItem(item?: QuotaPlanItem | null): QuotaPlanItem {
    return {
      ...(item ?? {}),
      chargeRules: [...(item?.chargeRules ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    };
  }

  private normalizePlan(plan?: QuotaPlan | null): QuotaPlan {
    const warningRules = this.normalizeWarningRules(plan?.warningRules ?? plan?.warningRule);

    return {
      ...(plan ?? {}),
      warningRules,
      items: [...(plan?.items ?? [])].map(item => this.normalizeItem(item)).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    };
  }

  private buildNormalizedPlans(plans: QuotaPlan[]): QuotaPlan[] {
    return [...plans]
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map(plan => ({
        ...plan,
        overviewStats: this.buildPlanOverviewStats(plan),
        collapsedSummaryStats: this.buildCollapsedSummaryStats(plan),
        warningSummary: this.buildWarningRuleSummary(plan.warningRules)
      }));
  }

  private buildWarningRuleSummary(warningRules?: QuotaPlanWarningRule[] | null): string {
    const thresholds = (warningRules ?? [])
      .map(rule => rule.thresholdPercent)
      .filter((value): value is number => typeof value === 'number')
      .sort((a, b) => a - b);

    if (!thresholds.length) {
      return '';
    }

    return thresholds.map(value => `${value}%`).join(' / ');
  }

  private cloneWarningRules(warningRules?: QuotaPlanWarningRule[] | null): QuotaPlanWarningRule[] {
    return (warningRules ?? []).map(rule => ({ ...rule }));
  }

  private createPlanInfoDraft(plan: QuotaPlan): PlanInfoDraft {
    return {
      planName: plan.planName ?? '',
      planDescription: plan.planDescription ?? '',
      status: plan.status ?? 'Enabled'
    };
  }

  private normalizeWarningRules(input?: QuotaPlanWarningRule[] | QuotaPlanWarningRule | null): QuotaPlanWarningRule[] {
    const rawRules = Array.isArray(input) ? input : input ? [input] : [];

    return rawRules
      .map(rule => ({
        thresholdPercent: typeof rule?.thresholdPercent === 'number' ? rule.thresholdPercent : undefined,
        titleTemplate: rule?.titleTemplate?.trim() ?? '',
        contentTemplate: rule?.contentTemplate?.trim() ?? '',
        status: rule?.status ?? 'Enabled',
        lastUpdateTime: rule?.lastUpdateTime
      }))
      .filter(rule => typeof rule.thresholdPercent === 'number' || !!rule.titleTemplate || !!rule.contentTemplate || !!rule.lastUpdateTime)
      .sort((a, b) => (a.thresholdPercent ?? 0) - (b.thresholdPercent ?? 0));
  }

  private preserveWarningRuleStatuses(
    source?: QuotaPlanWarningRule[] | QuotaPlanWarningRule | null,
    draft?: QuotaPlanWarningRule[] | QuotaPlanWarningRule | null
  ): QuotaPlanWarningRule[] {
    const sourceRules = this.normalizeWarningRules(source);
    const draftRules = this.normalizeWarningRules(draft);

    return draftRules.map((rule, index) => {
      const matchedRule = sourceRules.find(sourceRule => sourceRule.thresholdPercent === rule.thresholdPercent) ?? sourceRules[index];
      return {
        ...rule,
        status: matchedRule?.status ?? rule.status ?? 'Enabled'
      };
    });
  }

  private validateWarningRules(rules: QuotaPlanWarningRule[]): string | null {
    const duplicatedThresholds = new Set<number>();
    const seenThresholds = new Set<number>();

    for (const rule of rules) {
      if (typeof rule.thresholdPercent !== 'number' || Number.isNaN(rule.thresholdPercent)) {
        return this.t('quota.plan.validation.warning-threshold-required');
      }

      if (rule.thresholdPercent < 0 || rule.thresholdPercent > 100) {
        return this.t('quota.plan.validation.warning-threshold-range');
      }

      if (seenThresholds.has(rule.thresholdPercent)) {
        duplicatedThresholds.add(rule.thresholdPercent);
      }
      seenThresholds.add(rule.thresholdPercent);
    }

    if (duplicatedThresholds.size) {
      return this.t('quota.plan.validation.warning-threshold-duplicate', {
        values: `${[...duplicatedThresholds].sort((a, b) => a - b).join(' / ')}%`
      });
    }

    return null;
  }

  private getUnitLabel(unit?: string): string {
    const normalizedUnit = String(unit ?? '')
      .trim()
      .toLowerCase();
    const unitMap: Record<string, string> = {
      个: this.t('quota.unit.item'),
      item: this.t('quota.unit.item'),
      items: this.t('quota.unit.item'),
      人: this.t('quota.unit.person'),
      person: this.t('quota.unit.person'),
      people: this.t('quota.unit.person'),
      天: this.t('quota.unit.day'),
      day: this.t('quota.unit.day'),
      days: this.t('quota.unit.day'),
      组: this.t('quota.unit.group'),
      group: this.t('quota.unit.group'),
      groups: this.t('quota.unit.group'),
      gb: this.t('quota.unit.gb')
    };

    return unitMap[normalizedUnit] ?? (unit?.trim() || '');
  }

  private formatDisplayNumber(value?: string | number | null): string {
    const text = String(value ?? '').trim();
    if (!text) {
      return '--';
    }

    const normalizedText = text.replace(/,/g, '');
    const num = Number(normalizedText);
    if (!Number.isFinite(num) || !/^-?\d+(\.\d+)?$/.test(normalizedText)) {
      return text;
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  }

  private parseAuditData(value?: string): unknown {
    let current: unknown = String(value ?? '').trim();
    if (!current) {
      return null;
    }

    for (let i = 0; i < 3; i += 1) {
      if (typeof current !== 'string') {
        return current;
      }

      const text = current.trim();
      if (!text) {
        return null;
      }

      const looksLikeJson =
        (text.startsWith('{') && text.endsWith('}')) ||
        (text.startsWith('[') && text.endsWith(']')) ||
        (text.startsWith('"[') && text.endsWith(']"')) ||
        (text.startsWith('"{') && text.endsWith('}"'));

      if (!looksLikeJson) {
        return text;
      }

      try {
        current = JSON.parse(text);
      } catch {
        return text;
      }
    }

    return current;
  }

  private getAuditDiffKeys(oldData: Record<string, unknown>, newData: Record<string, unknown>): string[] {
    const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    const ordered = this.auditFieldOrder.filter(key => keys.has(key));
    const extra = [...keys].filter(key => key !== 'version' && !this.auditFieldOrder.includes(key)).sort((a, b) => a.localeCompare(b));
    return [...ordered, ...extra];
  }

  private getAuditFieldLabel(key: string): string {
    const labelMap: Record<string, string> = {
      freeLimitValue: this.t('quota.table.free-quota'),
      defaultValue: this.t('quota.detail.default-value'),
      chargePolicyType: this.t('quota.table.overflow-policy'),
      quotaMode: this.t('quota.detail.quota-mode'),
      chargeRules: this.t('quota.detail.audit.charge-rule'),
      status: this.t('quota.table.status'),
      version: this.t('quota.detail.audit.version')
    };

    return labelMap[key] ?? key;
  }

  private formatAuditFieldValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'freeLimitValue' || key === 'defaultValue') {
      return this.formatQuotaValue(String(value), undefined);
    }

    if (key === 'chargePolicyType') {
      return this.getChargePolicyLabel(String(value) as ChargePolicyType);
    }

    if (key === 'quotaMode') {
      const modeMap: Record<QuotaMode, string> = {
        PreCheckLimit: this.t('quota.mode.pre-check'),
        PostUsageBilling: this.t('quota.mode.post-usage'),
        RetentionPolicy: this.t('quota.mode.retention')
      };
      return modeMap[String(value) as QuotaMode] ?? String(value);
    }

    if (key === 'status') {
      return this.getStatusLabel(String(value) as PlanStatus);
    }

    return String(value);
  }

  private isAuditRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private isAuditRuleList(value: unknown): value is Array<Record<string, unknown>> {
    return Array.isArray(value);
  }

  private isAuditWarningRuleList(value: unknown): value is Array<Record<string, unknown>> {
    return (
      Array.isArray(value) &&
      value.some(item => this.isAuditRecord(item) && ('thresholdPercent' in item || 'titleTemplate' in item || 'contentTemplate' in item))
    );
  }

  private getChargeRuleAuditDiffRows(oldValue: unknown, newValue: unknown, fieldName?: string): QuotaAuditDiffRow[] {
    const oldRules = Array.isArray(oldValue) ? oldValue : [];
    const newRules = Array.isArray(newValue) ? newValue : [];
    const fieldOrder = [
      'name',
      'ruleType',
      'description',
      'unitPrice',
      'currency',
      'billingUnit',
      'effectValue',
      'effectUnit',
      'effectiveDate',
      'status',
      'recommend'
    ];

    return Array.from({ length: Math.max(oldRules.length, newRules.length) }, (_, index) => {
      const oldRule = this.isAuditRecord(oldRules[index]) ? oldRules[index] : {};
      const newRule = this.isAuditRecord(newRules[index]) ? newRules[index] : {};
      const ruleName = String(
        newRule['name'] ?? oldRule['name'] ?? `${fieldName || this.t('quota.detail.audit.charge-rule')} ${index + 1}`
      ).trim();
      return fieldOrder
        .map(key => {
          const oldFieldValue = this.formatChargeRuleAuditValue(key, oldRule[key]);
          const newFieldValue = this.formatChargeRuleAuditValue(key, newRule[key]);
          return {
            key: `chargeRules-${index}-${key}`,
            label: `${ruleName} / ${this.getChargeRuleAuditFieldLabel(key)}`,
            oldValue: oldFieldValue,
            newValue: newFieldValue,
            changed: oldFieldValue !== newFieldValue
          };
        })
        .filter(row => row.changed);
    }).flat();
  }

  private getWarningRuleAuditDiffRows(oldValue: unknown, newValue: unknown, fieldName?: string): QuotaAuditDiffRow[] {
    const oldRules = Array.isArray(oldValue) ? oldValue : [];
    const newRules = Array.isArray(newValue) ? newValue : [];
    const fieldOrder = ['thresholdPercent', 'titleTemplate', 'contentTemplate', 'status'];

    return Array.from({ length: Math.max(oldRules.length, newRules.length) }, (_, index) => {
      const oldRule = this.isAuditRecord(oldRules[index]) ? oldRules[index] : {};
      const newRule = this.isAuditRecord(newRules[index]) ? newRules[index] : {};
      const threshold = String(newRule['thresholdPercent'] ?? oldRule['thresholdPercent'] ?? `${index + 1}`).trim();
      const ruleName = `${fieldName || this.t('quota.detail.audit.warning-rule')} ${threshold}%`;
      return fieldOrder
        .map(key => {
          const oldFieldValue = this.formatWarningRuleAuditValue(key, oldRule[key]);
          const newFieldValue = this.formatWarningRuleAuditValue(key, newRule[key]);
          return {
            key: `warningRules-${index}-${key}`,
            label: `${ruleName} / ${this.getWarningRuleAuditFieldLabel(key)}`,
            oldValue: oldFieldValue,
            newValue: newFieldValue,
            changed: oldFieldValue !== newFieldValue
          };
        })
        .filter(row => row.changed);
    }).flat();
  }

  private getChargeRuleAuditFieldLabel(key: string): string {
    const labelMap: Record<string, string> = {
      name: this.t('quota.detail.audit.charge-field.name'),
      ruleType: this.t('quota.detail.audit.charge-field.rule-type'),
      description: this.t('quota.detail.audit.charge-field.description'),
      unitPrice: this.t('quota.detail.audit.charge-field.unit-price'),
      currency: this.t('quota.detail.audit.charge-field.currency'),
      billingUnit: this.t('quota.detail.audit.charge-field.billing-unit'),
      effectValue: this.t('quota.detail.audit.charge-field.effect-value'),
      effectUnit: this.t('quota.detail.audit.charge-field.effect-unit'),
      effectiveDate: this.t('quota.detail.audit.charge-field.effective-date'),
      status: this.t('quota.detail.audit.charge-field.status'),
      recommend: this.t('quota.detail.audit.charge-field.recommend')
    };

    return labelMap[key] ?? key;
  }

  private formatChargeRuleAuditValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'ruleType') {
      return this.getChargePolicyLabel(String(value) as ChargePolicyType);
    }

    if (key === 'unitPrice') {
      return this.formatDisplayNumber(String(value));
    }

    if (key === 'effectiveDate') {
      const timestamp = Number(value);
      return Number.isFinite(timestamp)
        ? new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }).format(new Date(timestamp))
        : String(value);
    }

    if (key === 'status') {
      return this.getStatusLabel(String(value) as PlanStatus);
    }

    if (key === 'recommend') {
      return value === true || String(value).toLowerCase() === 'true' ? this.t('common.yes') : this.t('common.no');
    }

    return String(value);
  }

  private getWarningRuleAuditFieldLabel(key: string): string {
    const labelMap: Record<string, string> = {
      thresholdPercent: this.t('quota.detail.audit.warning-field.threshold'),
      titleTemplate: this.t('quota.detail.audit.warning-field.title'),
      contentTemplate: this.t('quota.detail.audit.warning-field.content'),
      status: this.t('quota.detail.audit.warning-field.status')
    };

    return labelMap[key] ?? key;
  }

  private formatWarningRuleAuditValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'thresholdPercent') {
      return `${this.formatDisplayNumber(String(value))}%`;
    }

    if (key === 'status') {
      return this.getStatusLabel(String(value) as PlanStatus);
    }

    return String(value);
  }

  private applyPlanWarningRules(planId: string, warningRules: QuotaPlanWarningRule[]): void {
    this.plans = this.plans.map(plan => (plan.planId === planId ? { ...plan, warningRules: this.cloneWarningRules(warningRules) } : plan));
    this.normalizedPlans = this.buildNormalizedPlans(this.plans);
    this.cdr.markForCheck();
  }

  private applyPlanInfo(planId: string, info: Pick<PlanInfoDraft, 'planName' | 'planDescription'>): void {
    this.plans = this.plans.map(plan => (plan.planId === planId ? { ...plan, ...info } : plan));
    this.normalizedPlans = this.buildNormalizedPlans(this.plans);
    this.cdr.markForCheck();
  }
}
